import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Stack, Button, CircularProgress, Alert, Tabs, Tab,
  Avatar, alpha, useTheme,
} from '@mui/material';
import { ArrowBack, OpenInNew, Business, ViewModule, Payments, Campaign, Timeline as TimelineIcon, SupportAgent, Speed, People } from '@mui/icons-material';
import { getTenantById, type TenantConsoleListItem } from '../services/tenantService';
import { SoftChip, type SoftChipTone } from '../components/SoftChip';
import { TenantCatalogTab } from '../components/TenantCatalogTab';
import { TenantBillingTab } from '../components/TenantBillingTab';
import { TenantStrumentiTab } from '../components/strumenti/TenantStrumentiTab';
import { TettoAiAziendaCard } from '../components/strumenti/TettoAiAziendaCard';
import { TenantMarketingConfigTab } from '../components/TenantMarketingConfigTab';
import { TenantLifecycleTab } from '../components/TenantLifecycleTab';
import { AccessoAziendaCard } from '../components/aziende/AccessoAziendaCard';
import { TenantSessionEnforcementCard } from '../components/TenantSessionEnforcementCard';
import { TenantYumaCallTab } from '../components/TenantYumaCallTab';
import { TenantPersoneTab } from '../components/TenantPersoneTab';
import type { OnboardingStatus } from '@shared/types';

const APP_USER_URL = import.meta.env.VITE_APP_USER_URL || 'https://app.yumaos.com';

const STATUS_TONE: Record<OnboardingStatus, SoftChipTone> = {
  pending_email: 'warning',
  active_limited: 'info',
  active_full: 'success',
  suspended: 'error',
};

type TabKey = 'info' | 'persone' | 'catalog' | 'billing' | 'strumenti' | 'marketing' | 'lifecycle' | 'yumacall';
const TAB_VALIDE: TabKey[] = ['info', 'persone', 'catalog', 'billing', 'strumenti', 'marketing', 'lifecycle', 'yumacall'];

/** Estrae le iniziali dal nome azienda per l'avatar (max 2 char). */
function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '?';
}

export const TenantDetailPage: React.FC = () => {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tenant, setTenant] = useState<TenantConsoleListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // `?tab=strumenti` apre direttamente una scheda (la pagina «Consumi» di piattaforma rimanda qui).
  const [tab, setTab] = useState<TabKey>(() => {
    const chiesta = searchParams.get('tab');
    return TAB_VALIDE.includes(chiesta as TabKey) ? (chiesta as TabKey) : 'info';
  });

  const reload = useMemo(() => async () => {
    if (!id) return;
    try {
      const t = await getTenantById(id);
      if (!t) setError('Tenant non trovato');
      else setTenant(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, [reload]);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error || !tenant) return <Alert severity="error">{error || 'Tenant non trovato'}</Alert>;

  const status = tenant.onboarding_status;
  const addons = tenant.modules?.addon ?? [];

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        size="small"
        onClick={() => navigate('/tenants')}
        sx={{ mb: 2 }}
      >
        Torna ai tenant
      </Button>

      {/* Header card */}
      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar
              sx={{
                width: 56, height: 56,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.dark,
                fontWeight: 700, fontSize: '1.15rem',
              }}
            >
              {getInitials(tenant.nome_azienda)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {tenant.nome_azienda}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
                <SoftChip label={tenant.id} tone="default" mono />
                <SoftChip label={`tier: ${tenant.tier ?? 'full'}`} tone="primary" mono />
                {status && <SoftChip label={status} tone={STATUS_TONE[status]} />}
                <SoftChip
                  label={addons.length === 0 ? 'nessun add-on' : `${addons.length} add-on attiv${addons.length === 1 ? 'o' : 'i'}`}
                  tone={addons.length === 0 ? 'default' : 'success'}
                />
              </Stack>
            </Box>
          </Stack>
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNew />}
            onClick={() => window.open(`${APP_USER_URL}/admin/tenants/${tenant.id}`, '_blank')}
            sx={{ flexShrink: 0 }}
          >
            Apri nell'app utente
          </Button>
        </Stack>
      </Paper>

      {/* Tabs */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 1 }}>
          <Tab value="info" icon={<Business fontSize="small" />} iconPosition="start" label="Info" />
          <Tab value="persone" icon={<People fontSize="small" />} iconPosition="start" label="Persone" />
          <Tab value="catalog" icon={<ViewModule fontSize="small" />} iconPosition="start" label="Catalogo & Add-on" />
          <Tab value="billing" icon={<Payments fontSize="small" />} iconPosition="start" label="Billing & Stripe" />
          <Tab value="strumenti" icon={<Speed fontSize="small" />} iconPosition="start" label="Strumenti" />
          <Tab value="lifecycle" icon={<TimelineIcon fontSize="small" />} iconPosition="start" label="Lifecycle & Stato" />
          <Tab value="marketing" icon={<Campaign fontSize="small" />} iconPosition="start" label="Marketing AI" />
          <Tab value="yumacall" icon={<SupportAgent fontSize="small" />} iconPosition="start" label="YumaCALL" />
        </Tabs>
      </Paper>

      {/* Contenuto tab */}
      {tab === 'info' && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Informazioni tenant
          </Typography>
          <Stack spacing={1.5}>
            <InfoRow label="Nome azienda" value={tenant.nome_azienda} />
            <InfoRow label="P.IVA" value={tenant.partita_iva ?? '—'} mono />
            <InfoRow label="Email aziendale" value={tenant.email_aziendale ?? '—'} />
            <InfoRow label="Dominio email" value={tenant.email_domain ?? '—'} mono />
            <InfoRow label="Tier" value={tenant.tier ?? 'full'} mono />
            <InfoRow label="Onboarding status" value={tenant.onboarding_status ?? '—'} />
            <InfoRow label="ID Firestore" value={tenant.id} mono />
          </Stack>
        </Paper>
      )}

      {tab === 'catalog' && (
        <TenantCatalogTab tenant={tenant} onChange={reload} onApriStrumenti={() => setTab('strumenti')} />
      )}

      {tab === 'billing' && (
        <TenantBillingTab tenant={tenant} onChange={reload} />
      )}

      {tab === 'strumenti' && (
        <>
          <TenantStrumentiTab tenant={tenant} onChange={reload} />
          {/* Il tetto giornaliero delle funzioni AI di questa azienda (22/10/2026). */}
          <TettoAiAziendaCard tenant={tenant} />
        </>
      )}

      {tab === 'lifecycle' && (
        <>
          {/* Blocco dell'accesso dell'azienda intera, con resoconto e conferma (24/10/2026). */}
          <AccessoAziendaCard tenant={tenant} onChange={reload} />
          <TenantLifecycleTab tenant={tenant as any} onChange={reload} />
          {/* Limite sessioni anti-condivisione: override per-tenant + default piattaforma */}
          <TenantSessionEnforcementCard tenant={tenant as any} onChange={reload} />
        </>
      )}

      {tab === 'persone' && (
        <TenantPersoneTab tenant={tenant} />
      )}

      {tab === 'marketing' && (
        <TenantMarketingConfigTab tenantId={tenant.id} />
      )}

      {tab === 'yumacall' && (
        <TenantYumaCallTab tenant={tenant} onChange={reload} />
      )}
    </Box>
  );
};

const InfoRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 2 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : undefined }}>
      {value}
    </Typography>
  </Stack>
);
